import { useCallback, useState } from 'react';
import { MASSAGER_JA } from '@/shared/i18n/massager';

export interface Coords {
  latitude: number;
  longitude: number;
}

export interface GeolocationState {
  isFetching: boolean;
  error: string | null;
  coords: Coords | null;
}

/**
 * Promise-based wrapper around navigator.geolocation with friendly Japanese
 * error messages. Callers use `request()` to trigger an explicit permission
 * prompt — we never read position automatically.
 */
export function useGeolocation() {
  const [state, setState] = useState<GeolocationState>({
    isFetching: false,
    error: null,
    coords: null,
  });

  const request = useCallback((): Promise<Coords> => {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        const message = MASSAGER_JA.error.geolocationUnavailable;
        setState({ isFetching: false, error: message, coords: null });
        reject(new Error(message));
        return;
      }

      setState({ isFetching: true, error: null, coords: null });

      navigator.geolocation.getCurrentPosition(
        position => {
          const coords: Coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setState({ isFetching: false, error: null, coords });
          resolve(coords);
        },
        err => {
          let message: string = MASSAGER_JA.error.geolocationUnavailable;
          if (err.code === err.PERMISSION_DENIED) {
            message = MASSAGER_JA.error.geolocationDenied;
          } else if (err.code === err.TIMEOUT) {
            message = MASSAGER_JA.error.geolocationTimeout;
          }
          setState({ isFetching: false, error: message, coords: null });
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    });
  }, []);

  return { ...state, request };
}
